import type { CartItemSnapshot } from "../../domain/contracts/offline-sync";
import { posApi } from "../api/client";
import { excludeFreeProductRewardFromLoyaltyPreviewItems } from "./loyalty-preview-items";
import {
  resolveCustomerLoyaltyBalance,
  resolveEffectiveCustomerId,
} from "./loyalty-balance";
import { saveCustomerToReferenceCache } from "./offline-sync/reference-cache";
import type { LocalOrderCustomerSnapshot, LocalOrderRewardSnapshot } from "./offline-sync/types";

export type MemberReceiptSnapshot = {
  phone: string | null;
  firstName?: string;
  lastName?: string;
  /** ยอดก่อนสะสมจากบิลนี้ (local) หรือยอดรวมหลังสะสมแล้ว (server) — ดู balanceIncludesEarned */
  loyaltyPointsBalance: number;
  loyaltyPointsEarned?: number;
  /** คะแนนที่ใช้แลกรางวัล (local estimate หรือ server REDEEM) */
  loyaltyPointsRedeemed?: number;
  /** true = loyaltyPointsBalance รวมคะแนนครั้งนี้แล้ว (server receipt) */
  balanceIncludesEarned?: boolean;
};

/** ยอดคงเหลือที่แสดงบน bill = ก่อนหน้า + สะสมครั้งนี้ */
export function getMemberReceiptTotalBalance(receipt: {
  loyaltyPointsBalance: number;
  loyaltyPointsEarned?: number;
  loyaltyPointsRedeemed?: number;
  balanceIncludesEarned?: boolean;
}): number {
  const earned = receipt.loyaltyPointsEarned ?? 0;
  const redeemed = receipt.loyaltyPointsRedeemed ?? 0;
  if (receipt.balanceIncludesEarned === false) {
    return receipt.loyaltyPointsBalance - redeemed + earned;
  }
  return receipt.loyaltyPointsBalance;
}

function hasMemberIdentity(
  snapshot?: LocalOrderCustomerSnapshot,
  customerId?: string,
): boolean {
  if (resolveEffectiveCustomerId(customerId, snapshot)) return true;
  if (!snapshot) return false;
  return Boolean(
    snapshot.phone?.trim() ||
      snapshot.firstName?.trim() ||
      snapshot.lastName?.trim(),
  );
}

function isOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine;
}

export async function previewLoyaltyPointsEarned(
  customerId: string | undefined,
  cartItems: CartItemSnapshot[],
  rewardSnapshot?: LocalOrderRewardSnapshot | null,
): Promise<number> {
  const id = customerId?.trim();
  if (!id || !isOnline()) return 0;

  const items = excludeFreeProductRewardFromLoyaltyPreviewItems(
    cartItems.flatMap((item) =>
      item.variantId
        ? [
            {
              variantId: item.variantId,
              quantity: item.quantity,
              lineSubtotal: item.totalPrice,
            },
          ]
        : [],
    ),
    rewardSnapshot,
  );
  if (items.length === 0) return 0;

  try {
    const preview = await posApi.previewLoyaltyPoints({ customerId: id, items });
    return Math.max(0, preview.points ?? 0);
  } catch {
    return 0;
  }
}

export async function buildMemberReceiptSnapshotForLocalCompleteWithTimeout(
  input: Parameters<typeof buildMemberReceiptSnapshotForLocalComplete>[0],
  timeoutMs = 3_000,
): Promise<MemberReceiptSnapshot | undefined> {
  try {
    return await Promise.race([
      buildMemberReceiptSnapshotForLocalComplete(input),
      new Promise<undefined>((resolve) =>
        window.setTimeout(() => resolve(undefined), timeoutMs),
      ),
    ]);
  } catch {
    return undefined;
  }
}

export async function buildMemberReceiptSnapshotForLocalComplete(input: {
  branchId: string;
  customerId?: string;
  customerSnapshot?: LocalOrderCustomerSnapshot;
  cartItems: CartItemSnapshot[];
  loyaltyPointsRedeemed?: number;
  rewardSnapshot?: LocalOrderRewardSnapshot | null;
}): Promise<MemberReceiptSnapshot | undefined> {
  if (!hasMemberIdentity(input.customerSnapshot, input.customerId)) {
    return undefined;
  }

  const snapshot = input.customerSnapshot;
  const phone = snapshot?.phone?.trim();
  const effectiveCustomerId = resolveEffectiveCustomerId(
    input.customerId,
    snapshot,
  );

  const [balanceBefore, pointsEarned] = await Promise.all([
    resolveCustomerLoyaltyBalance({
      branchId: input.branchId,
      customerId: effectiveCustomerId,
      customerSnapshot: snapshot,
      phone,
      firstName: snapshot?.firstName,
      lastName: snapshot?.lastName,
    }),
    previewLoyaltyPointsEarned(
      effectiveCustomerId,
      input.cartItems,
      input.rewardSnapshot,
    ),
  ]);

  const redeemed = Math.max(0, input.loyaltyPointsRedeemed ?? 0);
  const balanceAfter = balanceBefore - redeemed + pointsEarned;

  if (phone && effectiveCustomerId) {
    try {
      await saveCustomerToReferenceCache(input.branchId, {
        customerId: effectiveCustomerId,
        phone,
        firstName: snapshot?.firstName,
        lastName: snapshot?.lastName,
        loyaltyPoints: balanceAfter,
      });
    } catch {
      // cache update is best-effort
    }
  }

  return {
    phone: phone ?? null,
    firstName: snapshot?.firstName,
    lastName: snapshot?.lastName,
    loyaltyPointsBalance: balanceBefore,
    loyaltyPointsRedeemed: redeemed > 0 ? redeemed : undefined,
    loyaltyPointsEarned: pointsEarned > 0 ? pointsEarned : undefined,
    balanceIncludesEarned: false,
  };
}

export async function resolveLocalMemberReceipt(order: {
  branchId: string;
  customerId?: string;
  customerSnapshot?: LocalOrderCustomerSnapshot;
  memberReceiptSnapshot?: MemberReceiptSnapshot;
  cartItems?: CartItemSnapshot[];
  rewardSnapshot?: LocalOrderRewardSnapshot | null;
}): Promise<MemberReceiptSnapshot | null> {
  const snapshot = order.customerSnapshot;
  const effectiveCustomerId = resolveEffectiveCustomerId(
    order.customerId,
    snapshot,
  );

  if (!hasMemberIdentity(snapshot, order.customerId)) {
    return null;
  }

  const phone = snapshot?.phone?.trim();
  const cartItems = order.cartItems ?? [];

  if (order.memberReceiptSnapshot) {
    return order.memberReceiptSnapshot;
  }

  const [balance, pointsEarned] = await Promise.all([
    resolveCustomerLoyaltyBalance({
      branchId: order.branchId,
    customerId: effectiveCustomerId,
      customerSnapshot: snapshot,
      phone,
      firstName: snapshot?.firstName,
      lastName: snapshot?.lastName,
    }),
    previewLoyaltyPointsEarned(
      effectiveCustomerId,
      cartItems,
      order.rewardSnapshot,
    ),
  ]);

  return {
    phone: phone ?? null,
    firstName: snapshot?.firstName,
    lastName: snapshot?.lastName,
    loyaltyPointsBalance: balance,
    loyaltyPointsEarned: pointsEarned > 0 ? pointsEarned : undefined,
    balanceIncludesEarned: false,
  };
}
